import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import axios from "axios";
import {
  BookOpen,
  Search,
  Calendar,
  ArrowRight,
  Tag,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Default articles if none exist
const defaultArticles = [
  {
    id: "1",
    title: "Passer de micro-entreprise à société : le guide complet",
    slug: "passer-micro-entreprise-societe",
    excerpt: "Quand et comment basculer de l'auto-entrepreneur vers une SAS ou EURL ? Avantages, démarches et optimisations.",
    content: "",
    category: "Statuts juridiques",
    image_url: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800",
    author: "Simon",
    published: true,
    created_at: "2024-01-15T10:00:00Z",
  },
  {
    id: "2",
    title: "Rupture conventionnelle + freelance : maximiser vos aides",
    slug: "rupture-conventionnelle-freelance-are",
    excerpt: "Comment négocier votre rupture co et optimiser vos droits France Travail tout en lançant votre activité freelance.",
    content: "",
    category: "Développement",
    image_url: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?w=800",
    author: "Lamya",
    published: true,
    created_at: "2024-01-10T10:00:00Z",
  },
  {
    id: "3",
    title: "Holding + société d'exploitation : la structure gagnante",
    slug: "holding-societe-exploitation-freelance",
    excerpt: "Pourquoi et comment créer une holding en tant que freelance ? Avantages fiscaux, patrimoniaux et stratégiques.",
    content: "",
    category: "Optimisation",
    image_url: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800",
    author: "Loic",
    published: true,
    created_at: "2024-01-05T10:00:00Z",
  },
  {
    id: "4",
    title: "Optimiser sa rémunération freelance : salaire vs dividendes",
    slug: "optimiser-remuneration-salaire-dividendes",
    excerpt: "La stratégie optimale pour réduire vos charges sociales et votre IR tout en maximisant vos revenus nets.",
    content: "",
    category: "Optimisation",
    image_url: "https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800",
    author: "Assia",
    published: true,
    created_at: "2024-01-01T10:00:00Z",
  },
  {
    id: "5",
    title: "Investir en immobilier avec votre holding freelance",
    slug: "investir-immobilier-holding-freelance",
    excerpt: "SCI, cash via holding, financement : comment les freelances peuvent accéder à l'immobilier rapidement.",
    content: "",
    category: "Diversification",
    image_url: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800",
    author: "Cyril",
    published: true,
    created_at: "2023-12-20T10:00:00Z",
  },
  {
    id: "6",
    title: "ARE et dividendes : le combo gagnant (validé France Travail)",
    slug: "are-dividendes-france-travail",
    excerpt: "Oui, c'est possible ! Comment percevoir l'ARE tout en versant des dividendes. Témoignage et démarches.",
    content: "",
    category: "Développement",
    image_url: "https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=800",
    author: "Marie",
    published: true,
    created_at: "2023-12-15T10:00:00Z",
  },
];

export default function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [visibleCount, setVisibleCount] = useState(6); // Afficher 6 articles par défaut
  const ARTICLES_PER_PAGE = 6; // Nombre d'articles à charger à chaque clic

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, []);

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/blog/articles?published_only=true`);
      if (response.data.length > 0) {
        setArticles(response.data);
      } else {
        setArticles(defaultArticles);
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
      setArticles(defaultArticles);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories(["Statuts juridiques", "Fiscalité", "Protection sociale"]);
    }
  };

  const filteredArticles = articles.filter((article) => {
    const matchesSearch =
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" || article.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Articles visibles (limités par visibleCount)
  const visibleArticles = filteredArticles.slice(0, visibleCount);
  const hasMoreArticles = filteredArticles.length > visibleCount;

  // Reset visibleCount quand les filtres changent
  useEffect(() => {
    setVisibleCount(6);
  }, [searchTerm, selectedCategory]);

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + ARTICLES_PER_PAGE);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="blog-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-[#52fbb0]/10 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <BookOpen className="h-4 w-4" />
              Blog & Ressources
            </div>
            <h1 className="font-manrope font-bold text-4xl md:text-5xl lg:text-6xl text-slate-900 mb-6">
              Conseils et guides pour{" "}
              <span className="gradient-text">freelances</span>
            </h1>
            <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
              Fiscalité, statuts juridiques, optimisation... Retrouvez tous nos articles 
              et guides pratiques pour réussir en freelance.
            </p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full md:w-96">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                type="text"
                placeholder="Rechercher un article..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 rounded-full border-slate-200"
                data-testid="blog-search"
              />
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant={selectedCategory === "all" ? "default" : "outline"}
                onClick={() => setSelectedCategory("all")}
                className={`rounded-full ${
                  selectedCategory === "all"
                    ? "bg-[#52fbb0] text-slate-900 hover:bg-[#34d399]"
                    : ""
                }`}
                data-testid="filter-all"
              >
                Tous
              </Button>
              {(categories.length > 0 ? categories : ["Statuts juridiques", "Fiscalité", "Protection sociale"]).map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  onClick={() => setSelectedCategory(cat)}
                  className={`rounded-full ${
                    selectedCategory === cat
                      ? "bg-[#52fbb0] text-slate-900 hover:bg-[#34d399]"
                      : ""
                  }`}
                  data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="overflow-hidden">
                  <Skeleton className="h-48 w-full" />
                  <CardContent className="p-6">
                    <Skeleton className="h-4 w-24 mb-4" />
                    <Skeleton className="h-6 w-full mb-2" />
                    <Skeleton className="h-4 w-full mb-1" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredArticles.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {visibleArticles.map((article, index) => (
                  <Card
                    key={article.id}
                    className="blog-card overflow-hidden group"
                    data-testid={`article-card-${index}`}
                  >
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={article.image_url || "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800"}
                        alt={article.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <Badge variant="secondary" className="bg-[#52fbb0]/10 text-slate-700 hover:bg-[#52fbb0]/20">
                          <Tag className="h-3 w-3 mr-1" />
                          {article.category}
                        </Badge>
                        <span className="text-sm text-slate-500 flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(article.created_at)}
                        </span>
                      </div>
                      <h3 className="font-manrope font-semibold text-xl text-slate-900 mb-3 group-hover:text-[#52fbb0] transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-slate-600 mb-4 line-clamp-2">{article.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-500">Par {article.author}</span>
                        <Link
                          to={`/blog/${article.slug}`}
                          className="text-[#52fbb0] font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all"
                          data-testid={`article-link-${index}`}
                        >
                          Lire
                          <ArrowRight className="h-4 w-4" />
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Bouton Afficher plus */}
              {hasMoreArticles && (
                <div className="text-center mt-12">
                  <Button
                    onClick={handleShowMore}
                    variant="outline"
                    className="rounded-full px-8 py-6 text-lg font-semibold border-2 hover:bg-[#52fbb0]/10 hover:border-[#52fbb0]"
                    data-testid="show-more-btn"
                  >
                    Afficher plus d'articles
                    <span className="ml-2 text-slate-500">
                      ({filteredArticles.length - visibleCount} restants)
                    </span>
                  </Button>
                </div>
              )}

              {/* Compteur d'articles */}
              <div className="text-center mt-6 text-slate-500 text-sm">
                {visibleCount >= filteredArticles.length 
                  ? `${filteredArticles.length} article${filteredArticles.length > 1 ? 's' : ''} affiché${filteredArticles.length > 1 ? 's' : ''}`
                  : `${visibleCount} sur ${filteredArticles.length} articles`
                }
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="font-manrope font-semibold text-xl text-slate-900 mb-2">
                Aucun article trouvé
              </h3>
              <p className="text-slate-600">
                Essayez de modifier vos critères de recherche.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 md:py-24 bg-slate-900" data-testid="blog-newsletter">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-white mb-4">
              Restez informé
            </h2>
            <p className="text-slate-300 text-lg mb-8">
              Recevez nos derniers articles et conseils directement dans votre boîte mail.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Votre email"
                className="h-12 rounded-full bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                data-testid="newsletter-email"
              />
              <Button className="bg-[#52fbb0] text-slate-900 hover:bg-[#34d399] font-semibold rounded-full px-8 h-12">
                S'inscrire
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
